import React, { useEffect, useState, useContext } from "react";
import {
  collection,
  addDoc,
  serverTimestamp,
  getDocs,
  doc,
  deleteDoc,
  updateDoc,
  onSnapshot,
  query,
} from "firebase/firestore";
import { db, storage, auth } from "../../Firebase_Config/firebaseConfig";
import { ref, deleteObject } from "firebase/storage";
import { Helmet } from "react-helmet-async";
import { useNavigate } from "react-router-dom";
import { AuthContext } from "../Login/AuthContext";
import { signOut } from "firebase/auth";
import UserNav from "../../Components/Navbars/UserNav";
import "./AdminPortal.css";
import axios from "axios";

const AdminPortal = ({ setAlertMsg = () => {}, setAlertColor = () => {} }) => {
  const BackendURL = "https://mailing-backend-qiqy.onrender.com";
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [userEmails, setUserEmails] = useState({});
  const [usersWithBlogs, setUsersWithBlogs] = useState([]);
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [activePage, setActivePage] = useState("dashboard"); // dashboard, users, or registrations
  const navigate = useNavigate();
  const { authenticated, isAdmin, setAuthenticated, setIsAdmin } =
    useContext(AuthContext);
  const [registrations, setRegistrations] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingJobId, setEditingJobId] = useState(null);
  const [viewOnly, setViewOnly] = useState(false);

  const [jobForm, setJobForm] = useState({
    title: "",
    description: "",
    salary: "",
    jobType: "",
    location: "",
    experience: "",
    isOpen: true,
    company: {
      jurident: false,
      valsco: false,
    },
  });

  const handleJobFormChange = (e) => {
    const { name, value, checked } = e.target;

    if (name === "jurident" || name === "valsco") {
      setJobForm((prev) => ({
        ...prev,
        company: {
          ...prev.company,
          [name]: checked,
        },
      }));
    } else if (name === "isOpen") {
      setJobForm((prev) => ({
        ...prev,
        isOpen: value === "true",
      }));
    } else {
      setJobForm((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  const resetJobForm = () => {
    setShowCreateForm(false);
    setEditingJobId(null);
    setViewOnly(false);
    setJobForm({
      title: "",
      description: "",
      salary: "",
      jobType: "",
      location: "",
      experience: "",
      isOpen: true,
      company: { jurident: false, valsco: false },
    });
  };

  const submitJob = async () => {
    if (!jobForm.company.jurident && !jobForm.company.valsco) {
      setAlertMsg("❌ Select at least one company");
      setAlertColor("red");
      return;
    }

    if (!jobForm.title.trim()) {
      setAlertMsg("❌ Job title is required");
      setAlertColor("red");
      return;
    }

    try {
      if (editingJobId) {
        // 🔄 UPDATE JOB
        await updateDoc(doc(db, "job-openings", editingJobId), {
          ...jobForm,
          updatedAt: serverTimestamp(),
        });

        setAlertMsg("✅ Job updated successfully");
      } else {
        // ➕ CREATE JOB
        await addDoc(collection(db, "job-openings"), {
          ...jobForm,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        });

        setAlertMsg("✅ Job created successfully");
      }

      setAlertColor("lightgreen");
      resetJobForm();
    } catch (err) {
      console.error(err);
      setAlertMsg("❌ Failed to save job");
      setAlertColor("red");
    }
  };

  // Redirect unauthorized users

  useEffect(() => {
    if (!authenticated || !isAdmin) return;

    const unsubscribe = onSnapshot(
      collection(db, "job-openings"),
      (snapshot) => {
        const data = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setJobs(data);
      },
    );

    return () => unsubscribe();
  }, [authenticated, isAdmin]);

  useEffect(() => {
    if (!authenticated || !isAdmin) navigate("/Login", { replace: true });
  }, [authenticated, isAdmin, navigate]);

  // Fetch blogs and users
  useEffect(() => {
    if (!authenticated || !isAdmin) return;

    const blogsQuery = query(collection(db, "blogPosts"));
    const usersQuery = query(collection(db, "users"));

    const fetchAllData = async () => {
      try {
        const usersSnapshot = await getDocs(usersQuery);
        const emails = {};
        const allUsers = [];
        usersSnapshot.forEach((doc) => {
          emails[doc.id] = doc.data().email;
          allUsers.push({ id: doc.id, ...doc.data() });
        });
        setUserEmails(emails);

        const unsubscribe = onSnapshot(
          blogsQuery,
          (snapshot) => {
            const allBlogs = snapshot.docs.map((doc) => ({
              id: doc.id,
              ...doc.data(),
              priority: doc.data().priority || 0,
            }));

            const visibleBlogs = allBlogs.filter(
              (b) => b.status?.trim().toLowerCase() !== "deleted",
            );

            visibleBlogs.sort((a, b) => {
              if (a.priority !== b.priority) return a.priority - b.priority;
              const dateA = a.publishDate?.toDate
                ? a.publishDate.toDate()
                : new Date(a.publishDate || 0);
              const dateB = b.publishDate?.toDate
                ? b.publishDate.toDate()
                : new Date(b.publishDate || 0);
              return dateB - dateA;
            });

            setBlogs(visibleBlogs);
            setLoading(false);

            const userIdsWithBlogs = new Set(
              visibleBlogs.map((blog) => blog.author?.id),
            );
            const filteredUsers = allUsers.filter((user) =>
              userIdsWithBlogs.has(user.id),
            );
            setUsersWithBlogs(filteredUsers);
          },
          (error) => {
            console.error(error);
            setAlertMsg("Failed to fetch blogs.");
            setAlertColor("red");
            setLoading(false);
          },
        );

        return () => unsubscribe();
      } catch (error) {
        console.error(error);
        setAlertMsg("Failed to fetch data.");
        setAlertColor("red");
        setLoading(false);
      }
    };

    fetchAllData();
  }, [authenticated, isAdmin, setAlertMsg, setAlertColor]);

  // Fetch course registrations with real-time updates
  useEffect(() => {
    if (!authenticated || !isAdmin) return;

    const unsubscribe = onSnapshot(
      collection(db, "course-registration"),
      (snapshot) => {
        const data = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setRegistrations(data);
      },
      (error) => {
        console.error("Error fetching course registrations: ", error);
        setAlertMsg("Failed to fetch course registrations.");
        setAlertColor("red");
      },
    );

    return () => unsubscribe();
  }, [authenticated, isAdmin, setAlertMsg, setAlertColor]);

  // Blog status update
  const updateBlogStatus = async (blogId, newStatus, name, email) => {
    try {
      await updateDoc(doc(db, "blogPosts", blogId), { status: newStatus });
      setAlertMsg(`Blog status updated to "${newStatus}".`);
      setAlertColor("lightgreen");

      if (newStatus === "approved") {
        await axios.post(`${BackendURL}/blog-approval`, { name, email });
      } else if (newStatus === "rejected") {
        await axios.post(`${BackendURL}/blog-rejection`, { name, email });
      }
    } catch (error) {
      console.error(error);
      setAlertMsg("Failed to update blog status or send email.");
      setAlertColor("red");
    }
  };

  // Blog priority update
  const updateBlogPriority = async (blogId, newPriority) => {
    const numericPriority = parseInt(newPriority, 10);
    if (isNaN(numericPriority)) return;

    const blogToUpdate = blogs.find((b) => b.id === blogId);
    if (!blogToUpdate) return;

    const blogToSwap = blogs.find((b) => b.priority === numericPriority);
    try {
      if (blogToSwap && blogToSwap.id !== blogId) {
        await updateDoc(doc(db, "blogPosts", blogToSwap.id), {
          priority: blogToUpdate.priority,
        });
      }
      await updateDoc(doc(db, "blogPosts", blogId), {
        priority: numericPriority,
      });
      setAlertMsg(`Blog priority updated to "${numericPriority}".`);
      setAlertColor("lightgreen");
    } catch (error) {
      console.error(error);
      setAlertMsg("Failed to update blog priority.");
      setAlertColor("red");
    }
  };

  // Delete blog
  const deleteBlog = async (blog) => {
    try {
      if (blog.ImgPath) {
        const imageRef = ref(storage, blog.ImgPath);
        await deleteObject(imageRef);
      }
      await deleteDoc(doc(db, "blogPosts", blog.id));
      setAlertMsg("🗑️ Blog deleted successfully!");
      setAlertColor("red");
    } catch (error) {
      console.error(error);
      setAlertMsg("❌ Failed to delete blog.");
      setAlertColor("red");
    }
  };

  // Function to delete a registration
  const deleteRegistration = async (registrationId) => {
    if (!registrationId) {
      setAlertMsg("Invalid registration ID.");
      setAlertColor("red");
      return;
    }
    try {
      await deleteDoc(doc(db, "course-registration", registrationId));
      setAlertMsg("🗑️ Registration deleted successfully!");
      setAlertColor("red");
    } catch (error) {
      console.error("Error deleting registration: ", error);
      setAlertMsg("❌ Failed to delete registration.");
      setAlertColor("red");
    }
  };

  // View blog
  const viewBlog = (blogId) => {
    if (!blogId) {
      setAlertMsg("Invalid blog ID.");
      setAlertColor("red");
      return;
    }
    navigate(`/BlogPage/${blogId}`);
  };

  // Logout
  const handleLogout = async () => {
    try {
      await signOut(auth);
      setAuthenticated(false);
      setIsAdmin(false);
      localStorage.setItem("authenticated", "false");
      localStorage.setItem("isAdmin", "false");
      navigate("/Login", { replace: true });
    } catch (error) {
      console.error(error);
      setAlertMsg("Failed to log out.");
      setAlertColor("red");
    }
  };

  // Helper to get the page title
  const getPageTitle = () => {
    switch (activePage) {
      case "dashboard":
        return "Dashboard";
      case "users":
        return "User Details";
      // Restored this case
      case "registrations":
        return "User Registrations";
      case "jobs":
        return "Job Openings";
      default:
        return "Admin";
    }
  };

  if (loading) {
    return (
      <div className="loader">
        <div className="lds-ring">
          <div></div>
          <div></div>
          <div></div>
          <div></div>
        </div>
      </div>
    );
  }

  return (
    <>
      <UserNav
        authenticated={authenticated}
        setAuthenticated={setAuthenticated}
        isAdmin={isAdmin}
        setIsAdmin={setIsAdmin}
        setAlertMsg={setAlertMsg}
        setAlertColor={setAlertColor}
        handleLogout={handleLogout}
      />
      <Helmet>
        <title>Admin Panel | VALSCO</title>
        <meta
          name="description"
          content="Admin dashboard for managing blogs and user registrations."
        />
      </Helmet>

      <div className="admin-page admin-portal-container">
        <aside className={`admin-sidebar ${isSidebarOpen ? "open" : "closed"}`}>
          <div className="sidebar-header">
            <h3 className="sidebar-title">Admin</h3>

            <button
              className="sidebar-close-btn"
              onClick={() => setIsSidebarOpen(false)}
              aria-label="Close sidebar"
            >
              ✕
            </button>
          </div>

          <ul className="sidebar-nav">
            <li className={activePage === "dashboard" ? "active" : ""}>
              <button onClick={() => setActivePage("dashboard")}>
                Dashboard
              </button>
            </li>
            <li className={activePage === "users" ? "active" : ""}>
              <button onClick={() => setActivePage("users")}>
                User Details
              </button>
            </li>
            {/* Restored this sidebar link */}
            <li className={activePage === "registrations" ? "active" : ""}>
              <button onClick={() => setActivePage("registrations")}>
                User Registrations
              </button>
            </li>
            <li className={activePage === "jobs" ? "active" : ""}>
              <button onClick={() => setActivePage("jobs")}>
                Job Openings
              </button>
            </li>
          </ul>
        </aside>

        <main className="admin-portal-body">
          <div className="dashboard-content">
            <div className="header">
              <button
                className="sidebar-toggle-btn"
                onClick={() => setIsSidebarOpen(!isSidebarOpen)}
              >
                &#9776;
              </button>
              <h2>{getPageTitle()}</h2>
            </div>

            {/* Dashboard Table */}
            {activePage === "dashboard" && (
              <div className="blog-list-admin">
                {blogs.length === 0 ? (
                  <p className="no-blogs-msg">No blogs available.</p>
                ) : (
                  <table>
                    <thead>
                      <tr>
                        <th>S.NO</th>
                        <th>Title</th>
                        <th>Author</th>
                        <th>Date</th>
                        <th>Status</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {blogs.map((blog) => (
                        <tr key={blog.id}>
                          <td>
                            <input
                              type="number"
                              className="priority-input"
                              value={blog.priority}
                              onChange={(e) =>
                                updateBlogPriority(blog.id, e.target.value)
                              }
                            />
                          </td>
                          <td>{blog.title || "Untitled"}</td>
                          <td>{blog.author?.name || "Unknown"}</td>
                          <td>
                            {blog.publishDate?.toDate
                              ? blog.publishDate.toDate().toLocaleString()
                              : blog.publishDate
                                ? new Date(blog.publishDate).toLocaleString()
                                : "N/A"}
                          </td>
                          <td>
                            <span
                              className={`status-badge ${
                                blog.status?.trim().toLowerCase() || "pending"
                              }`}
                            >
                              {blog.status?.trim() || "pending"}
                            </span>
                          </td>
                          <td className="actions-cell">
                            <button
                              onClick={() => viewBlog(blog.id)}
                              className="action-btn view-btn"
                              title="View Blog"
                            >
                              View
                            </button>
                            {["approved", "rejected"].includes(
                              blog.status?.trim().toLowerCase(),
                            ) && (
                              <button
                                onClick={() => deleteBlog(blog)}
                                className="action-btn delete-btn"
                              >
                                Delete
                              </button>
                            )}
                            {blog.status?.trim().toLowerCase() ===
                              "pending" && (
                              <>
                                <button
                                  onClick={() =>
                                    updateBlogStatus(
                                      blog.id,
                                      "approved",
                                      blog.author?.name || "Author",
                                      userEmails[blog.author?.id] || "",
                                    )
                                  }
                                  className="action-btn approve-btn"
                                >
                                  Approve
                                </button>
                                <button
                                  onClick={() =>
                                    updateBlogStatus(
                                      blog.id,
                                      "rejected",
                                      blog.author?.name || "Author",
                                      userEmails[blog.author?.id] || "",
                                    )
                                  }
                                  className="action-btn delete-btn"
                                >
                                  Reject
                                </button>
                              </>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            )}

            {/* User Details Table */}
            {activePage === "users" && (
              <div className="user-details">
                {usersWithBlogs.length === 0 ? (
                  <p className="no-blogs-msg">
                    No users have posted blogs yet.
                  </p>
                ) : (
                  <table>
                    <thead>
                      <tr>
                        <th>S.NO</th>
                        <th>User Name</th>
                        <th>Email</th>
                        <th>Blog Titles</th>
                      </tr>
                    </thead>
                    <tbody>
                      {usersWithBlogs.map((user, index) => {
                        const userBlogs = blogs.filter(
                          (blog) => blog.author?.id === user.id,
                        );
                        return (
                          <tr key={user.id}>
                            <td>{index + 1}</td>
                            <td>{user.name || "Unknown"}</td>
                            <td>{userEmails[user.id] || "N/A"}</td>
                            <td>
                              <ul style={{ paddingLeft: "1rem", margin: 0 }}>
                                {userBlogs.map((blog) => (
                                  <li key={blog.id}>
                                    <button
                                      style={{ background: "none", border: "none", padding: 0, color: "inherit", textDecoration: "underline", cursor: "pointer", font: "inherit" }}
                                      onClick={() => {
                                        viewBlog(blog.id);
                                      }}
                                    >
                                      {blog.title || "Untitled"}
                                    </button>
                                  </li>
                                ))}
                              </ul>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
              </div>
            )}

            {/* User Registrations Table */}
            {activePage === "registrations" && (
              <div className="user-registrations">
                {registrations.length === 0 ? (
                  <p className="no-blogs-msg">No user registrations found.</p>
                ) : (
                  <table>
                    <thead>
                      <tr>
                        <th>S.NO</th>
                        <th>Full Name</th>
                        <th>Email</th>
                        <th>Phone</th>
                        <th>Study Field</th>
                        {/* NEW: Course Name Header Added */}
                        <th>Course Name</th>
                        {/* <th>Birthday</th> */}
                        <th>City</th>
                        {/* <th>Nationality</th> */}
                        <th>Fees Paid</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {registrations.map((reg, index) => (
                        <tr key={reg.id}>
                          <td>{index + 1}</td>
                          <td>
                            {`${reg.fname || ""} ${reg.lname || ""}`.trim() ||
                              "N/A"}
                          </td>
                          <td>{reg.email || "N/A"}</td>
                          <td>{reg.phone || "N/A"}</td>
                          <td>{reg.studyField || "N/A"}</td>
                          {/* NEW: Course Name Data Cell Added */}
                          <td>{reg.courseName || "N/A"}</td>
                          {/* <td>{reg.birthday || "N/A"}</td> */}
                          <td>{reg.city || "N/A"}</td>
                          {/* <td>{reg.nationality || "N/A"}</td> */}
                          <td>{reg.feesPaid ? "Yes" : "No"}</td>
                          <td className="actions-cell">
                            <button
                              onClick={() => deleteRegistration(reg.id)}
                              className="action-btn delete-btn"
                              title="Delete Registration"
                            >
                              Delete
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            )}
            {activePage === "jobs" && (
              <div className="blog-list-admin">
                {/* Header */}

                <div className="header-actions">
                  <button
                    className="action-btn approve-btn"
                    onClick={() => {
                      resetJobForm(); // clears old data
                      setViewOnly(false);
                      setEditingJobId(null);
                      setShowCreateForm(true);
                    }}
                  >
                    + Create Job
                  </button>
                </div>

                <div
                  className={`job-form-card ${
                    showCreateForm ? "show" : "hide"
                  }`}
                >
                  <div className="job-field">
                    <label className="job-label">Job Title</label>
                    <input
                      type="text"
                      name="title"
                      placeholder="Job Title"
                      value={jobForm.title}
                      disabled={viewOnly}
                      onChange={handleJobFormChange}
                      className="job-input"
                    />
                  </div>

                  <div className="job-field">
                    <label className="job-label">Job Description</label>
                    <textarea
                      name="description"
                      placeholder="Job Description"
                      value={jobForm.description}
                      disabled={viewOnly}
                      onChange={handleJobFormChange}
                      className="job-textarea"
                      rows={4}
                    />
                  </div>

                  <div className="job-field">
                    <label className="job-label">Job Type</label>
                    <select
                      name="jobType"
                      value={jobForm.jobType}
                      onChange={handleJobFormChange}
                      disabled={viewOnly}
                      className="job-input"
                    >
                      <option value="">Select Job Type</option>
                      <option value="Full-time">Full-time</option>
                      <option value="Part-time">Part-time</option>
                      <option value="Internship">Internship</option>
                      <option value="Contract">Contract</option>
                    </select>
                  </div>

                  <div className="job-field">
                    <label className="job-label">Location</label>
                    <input
                      type="text"
                      name="location"
                      placeholder="Location (e.g. Remote / Delhi / Bangalore)"
                      value={jobForm.location}
                      disabled={viewOnly}
                      onChange={handleJobFormChange}
                      className="job-input"
                    />
                  </div>

                  <div className="job-field">
                    <label className="job-label">Experience</label>
                    <input
                      type="text"
                      name="experience"
                      placeholder="Experience"
                      value={jobForm.experience}
                      disabled={viewOnly}
                      onChange={handleJobFormChange}
                      className="job-input"
                    />
                  </div>

                  <div className="job-form-row">
                    <div className="job-field">
                      <label className="job-label">Salary</label>
                      <input
                        type="text"
                        name="salary"
                        placeholder="Salary"
                        value={jobForm.salary}
                        disabled={viewOnly}
                        onChange={handleJobFormChange}
                        className="job-input"
                      />
                    </div>

                    <div className="job-field">
                      <label className="job-label">Status</label>
                      <select
                        name="isOpen"
                        value={jobForm.isOpen ? "true" : "false"}
                        disabled={viewOnly}
                        onChange={handleJobFormChange}
                        className="job-input"
                      >
                        <option value="true">Active</option>
                        <option value="false">Inactive</option>
                      </select>
                    </div>

                    <div className="job-field">
                      <label className="job-label">Company</label>
                      <div className="company-checkbox">
                        <label>
                          <input
                            type="checkbox"
                            name="jurident"
                            disabled={viewOnly}
                            checked={jobForm.company.jurident}
                            onChange={handleJobFormChange}
                          />{" "}
                          Jurident
                        </label>

                        <label>
                          <input
                            type="checkbox"
                            name="valsco"
                            disabled={viewOnly}
                            checked={jobForm.company.valsco}
                            onChange={handleJobFormChange}
                          />{" "}
                          Valsco
                        </label>
                      </div>
                    </div>
                  </div>

                  <div className="job-form-actions">
                    {!viewOnly && (
                      <button
                        className="action-btn approve-btn"
                        onClick={submitJob}
                      >
                        {editingJobId ? "Update Job" : "Save Job"}
                      </button>
                    )}

                    <button
                      className="action-btn delete-btn"
                      onClick={resetJobForm}
                    >
                      {viewOnly ? "Close" : "Cancel"}
                    </button>
                  </div>
                </div>

                {/* JOBS TABLE */}
                <table
                  className={`jobs-table ${showCreateForm ? "dimmed" : ""}`}
                >
                  <thead>
                    <tr>
                      <th>S.NO</th>
                      <th>Title</th>
                      <th>Job Type</th>
                      <th>Location</th>
                      <th>Experience</th>
                      <th>Salary</th>
                      <th>Status</th>
                      <th>Company</th>
                      <th>Actions</th>
                    </tr>
                  </thead>

                  <tbody>
                    {jobs.length === 0 ? (
                      <tr>
                        <td colSpan="9" className="no-blogs-msg">
                          No job openings yet.
                        </td>
                      </tr>
                    ) : (
                      jobs.map((job, index) => (
                        <tr key={job.id}>
                          <td>{index + 1}</td>
                          <td>{job.title}</td>
                          <td>{job.jobType || "—"}</td>
                          <td>{job.location || "—"}</td>
                          <td>{job.experience || "—"}</td>
                          <td>{job.salary || "—"}</td>
                          <td>
                            <span
                              className={`status-badge ${
                                job.isOpen ? "approved" : "rejected"
                              }`}
                            >
                              {job.isOpen ? "Active" : "Inactive"}
                            </span>
                          </td>
                          <td>
                            {job.company?.jurident && "Jurident "}
                            {job.company?.valsco && "Valsco"}
                          </td>
                          <td className="actions-cell">
                            {/* VIEW */}
                            <button
                              className="action-btn view-btn"
                              onClick={() => {
                                setViewOnly(true);
                                setEditingJobId(job.id);
                                setJobForm(job);
                                setShowCreateForm(true);
                              }}
                            >
                              View
                            </button>

                            {/* EDIT */}
                            <button
                              className="action-btn approve-btn"
                              onClick={() => {
                                setViewOnly(false);
                                setEditingJobId(job.id);
                                setJobForm(job);
                                setShowCreateForm(true);
                              }}
                            >
                              Edit
                            </button>

                            {/* ACTIVATE / DEACTIVATE */}
                            <button
                              className="action-btn approve-btn"
                              onClick={() =>
                                updateDoc(doc(db, "job-openings", job.id), {
                                  isOpen: !job.isOpen,
                                  updatedAt: serverTimestamp(),
                                })
                              }
                            >
                              {job.isOpen ? "Deactivate" : "Activate"}
                            </button>

                            {/* DELETE */}
                            <button
                              className="action-btn delete-btn"
                              onClick={() =>
                                deleteDoc(doc(db, "job-openings", job.id))
                              }
                            >
                              Delete
                            </button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </main>
      </div>
    </>
  );
};

export default AdminPortal;
